/* B10 · One-Click Lesson Generator
 *
 * Teacher types a single prompt ("Grade 8 Biology · Photosynthesis") and we
 * generate 9 artifacts in parallel: lesson plan, slides, whiteboard diagram,
 * quiz, worksheet, homework, revision notes, exit ticket, answer key.
 *
 * The backend runs the generation as a background job and streams status via
 * a single GET poll every 1.5s. This file renders the intake form, the live
 * progress dashboard (9 status cards), and per-item detail viewers.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fmtDateTime } from "@/lib/dateUtils";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Wand2, Loader2, CheckCircle2, XCircle, ChevronRight, ClipboardCheck,
  BookOpen, Layers, ScrollText, PenTool, ClipboardList, NotebookText, Ticket, Key,
  X, RefreshCw, Send, Copy, FileText, Bookmark, GraduationCap, CreditCard,
} from "lucide-react";
import { SubjectPicker, GradePicker } from "@/components/CurriculumPicker";
import GenerateLoader from "@/components/ui/GenerateLoader";
import MobiHint from "@/components/MobiHint";
import MathText from "@/components/MathText";
import StoryQuizPlayer from "@/components/quiz/StoryQuizPlayer";

const ITEM_META = {
  lesson_plan: { icon: BookOpen,       tint: "#0EA5E9" },
  slides:      { icon: Layers,         tint: "#7C3AED" },
  whiteboard:  { icon: PenTool,        tint: "#F97316" },
  quiz:        { icon: ScrollText,     tint: "#FFB400" },
  worksheet:   { icon: ClipboardList,  tint: "#059669" },
  homework:    { icon: FileText,       tint: "#DB2777" },
  assignment:  { icon: GraduationCap,  tint: "#B45309" },
  flashcards:  { icon: CreditCard,     tint: "#DC2626" },
  revision:    { icon: NotebookText,   tint: "#6366F1" },
  exit_ticket: { icon: Ticket,         tint: "#0891B2" },
  answer_key:  { icon: Key,            tint: "#B45309" },
};

const POLL_MS = 1600;

export default function LessonBundle() {
  const [form, setForm] = useState({ prompt: "", grade: "", subject: "", subject_id: "", duration_minutes: 45, quiz_style: "mixed" });
  const [starting, setStarting] = useState(false);
  const [bundleId, setBundleId] = useState("");
  const [bundle, setBundle] = useState(null);
  const [openItem, setOpenItem] = useState(null); // { key, ... }
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachingSubject, setAttachingSubject] = useState(false);
  const pollRef = useRef(null);

  const attachToSubject = async () => {
    setAttachingSubject(true);
    try {
      const { data } = await api.post(`/ai/lesson-bundle/${bundleId}/attach-subject`, {});
      toast.success(`Attached ${data.attached} artifacts to ${data.subject_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    } finally { setAttachingSubject(false); }
  };

  // fetch bundle repeatedly while running
  useEffect(() => {
    if (!bundleId) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/ai/lesson-bundle/${bundleId}`);
        setBundle(data);
        if (data.status === "running") {
          pollRef.current = setTimeout(tick, POLL_MS);
        } else {
          const ok = (data.items || []).filter((i) => i.status === "done").length;
          const fail = (data.items || []).filter((i) => i.status === "failed").length;
          if (fail === 0) toast.success(`Lesson pack ready — ${ok} artifacts`);
          else toast.warning(`${ok} artifacts ready · ${fail} failed`);
        }
      } catch (e) {
        console.error(e);
        pollRef.current = setTimeout(tick, POLL_MS * 3);
      }
    };
    tick();
    return () => clearTimeout(pollRef.current);
  }, [bundleId]);

  const start = async (e) => {
    e?.preventDefault();
    if (form.prompt.trim().length < 6) return toast.error("Give me a topic — at least a few words.");
    setStarting(true);
    try {
      const QUIZ_STYLE_MAP = {
        mixed: ["mcq", "true_false", "short_answer"],
        mcq: ["mcq"],
        true_false: ["true_false"],
        fill_blank: ["fill_blank"],
        short_answer: ["short_answer"],
      };
      const { data } = await api.post("/ai/lesson-bundle", { ...form, quiz_types: QUIZ_STYLE_MAP[form.quiz_style] || [] });
      setBundleId(data.bundle_id);
      setBundle(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    } finally { setStarting(false); }
  };

  const reset = () => {
    clearTimeout(pollRef.current);
    setBundleId(""); setBundle(null); setOpenItem(null);
  };

  const items = bundle?.items || [];
  const doneCount = items.filter((i) => i.status === "done").length;
  const failedCount = items.filter((i) => i.status === "failed").length;
  const savedCount = items.filter((i) => i.saved_ref?.id).length;
  const progressPct = items.length ? Math.round((doneCount + failedCount) / items.length * 100) : 0;

  return (
    <div className="space-y-8" data-testid="lesson-bundle-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1">/ flagship · one-click</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter inline-flex items-center gap-2">
          <Wand2 size={28} className="text-[#FFB400]" /> One-Click Lesson Generator
        </h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
          Type <strong>one topic</strong>. Get a lesson plan, slides, whiteboard diagram, quiz, worksheet, homework,
          revision notes, exit ticket and answer key — all editable, all in one shot.
        </p>
      </div>

      {/* Intake form — hidden once a bundle is running/complete */}
      {!bundleId && (
        <form onSubmit={start} className="bg-white border border-black/8 rounded-2xl p-6 lg:p-8 space-y-4" data-testid="lesson-bundle-form">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Prompt</div>
            <input
              autoFocus
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              placeholder='e.g. "Grade 8 Biology — Photosynthesis"'
              data-testid="lb-prompt"
              className="w-full bg-white border border-black/15 rounded-md px-4 py-3 text-lg outline-none focus:border-[#0A0C10]"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Grade (optional)</div>
              <GradePicker value={form.grade} onChange={(v) => setForm({ ...form, grade: v })} data-testid="lb-grade" />
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Subject (auto-files all outputs)</div>
              <SubjectPicker
                value={form.subject_id}
                onChange={(sid, sname, _gid, gname) => setForm({ ...form, subject_id: sid, subject: sname, grade: gname || form.grade })}
                testid="lb-subject"
              />
              <div className="text-[10px] font-mono text-neutral-400 mt-1">
                Every generated artifact lands under this subject&rsquo;s dashboard automatically.
              </div>
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Duration</div>
              <select
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                data-testid="lb-duration"
                className="w-full bg-white border border-black/15 rounded-md px-3 py-2.5 text-sm outline-none"
              >
                {[30, 40, 45, 60, 75, 90].map((m) => <option key={m} value={m}>{m} minutes</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Quiz style</div>
            <div className="flex flex-wrap gap-2" data-testid="lb-quiz-style">
              {[["mixed", "Mixed"], ["mcq", "MCQ"], ["true_false", "True / False"], ["fill_blank", "Fill blanks"], ["short_answer", "Short answer"]].map(([v, lbl]) => (
                <button
                  key={v} type="button"
                  onClick={() => setForm({ ...form, quiz_style: v })}
                  data-testid={`lb-quiz-style-${v}`}
                  className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${form.quiz_style === v ? "bg-[#0A0C10] text-white border-transparent" : "border-black/15 hover:border-[#0A0C10]"}`}
                >{lbl}</button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={starting}
            data-testid="lb-generate"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-[#0A0C10] text-[#FFD600] font-bold disabled:opacity-50 hover:bg-[#0A0C10]/95"
          >
            {starting
              ? <GenerateLoader label="Kicking off" phrases={["Contacting Mobi…", "Setting up your pack…"]} />
              : <><Sparkles size={16} /> Generate the whole pack (11 artifacts)</>}
          </button>
        </form>
      )}

      {/* Live dashboard */}
      {bundleId && bundle && (
        <div className="space-y-6" data-testid="lesson-bundle-dashboard">
          <div className="bg-white border border-black/8 rounded-2xl p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500">/ bundle</div>
                <div className="font-display font-bold text-2xl mt-1">{bundle.topic || bundle.prompt}</div>
                <div className="mt-1 text-sm text-neutral-500">
                  {bundle.grade} · {bundle.subject} · {bundle.duration_minutes} min ·
                  <span className={`ml-2 inline-flex items-center gap-1 font-mono uppercase text-[10px] tracking-wider px-2 py-0.5 rounded ${bundle.status === "completed" ? "bg-emerald-100 text-emerald-700" : bundle.status === "partial" ? "bg-amber-100 text-amber-700" : bundle.status === "failed" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700 animate-pulse"}`}>
                    {bundle.status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {bundle.subject_id && (
                  <button
                    onClick={attachToSubject}
                    disabled={savedCount === 0 || attachingSubject}
                    data-testid="lb-attach-subject-btn"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-[#0A0C10] text-white hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Layers size={11} /> {attachingSubject ? "Attaching…" : `Attach to ${bundle.subject || "subject"}`}
                  </button>
                )}
                <button
                  onClick={() => setAttachOpen(true)}
                  disabled={savedCount === 0}
                  data-testid="lb-attach-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-[#FFD600] text-[#0A0C10] hover:bg-[#FFC600] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Bookmark size={11} /> Attach to a class
                </button>
                <button onClick={reset} data-testid="lb-reset" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-black/15 hover:border-[#0A0C10]">
                  <RefreshCw size={11} /> New bundle
                </button>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4">
              <div className="h-1.5 w-full bg-neutral-100 rounded overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#FFD600] via-[#FF7AD9] to-[#7C3AED]"
                  initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} transition={{ duration: 0.5 }}
                />
              </div>
              <div className="mt-1.5 text-[10px] font-mono uppercase tracking-wider text-neutral-500 flex justify-between">
                <span>{doneCount} / {items.length} ready {failedCount > 0 && <span className="text-red-600">· {failedCount} failed</span>}</span>
                <span>{progressPct}%</span>
              </div>
            </div>
          </div>

          {/* Item grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it) => <ItemCard key={it.key} item={it} onOpen={() => setOpenItem(it)} />)}
          </div>
        </div>
      )}

      {openItem && <ItemViewer item={openItem} onClose={() => setOpenItem(null)} />}
      {attachOpen && (
        <AttachToClassModal
          bundleId={bundleId}
          savedCount={savedCount}
          onClose={() => setAttachOpen(false)}
        />
      )}
      <MobiHint label="Ask Mobi for a topic idea" prompt="Suggest a rich single-topic prompt for my next 45-minute class." />
    </div>
  );
}

/* ────────────────────────── Item card ────────────────────────── */
function ItemCard({ item, onOpen }) {
  const meta = ITEM_META[item.key] || { icon: FileText, tint: "#0A0C10" };
  const Icon = meta.icon;
  const ready = item.status === "done";
  const failed = item.status === "failed";
  const pending = item.status === "pending";
  return (
    <motion.button
      layout
      onClick={ready ? onOpen : undefined}
      disabled={!ready}
      data-testid={`lb-item-${item.key}`}
      data-status={item.status}
      className={`text-left bg-white border rounded-xl p-5 transition-all ${ready
        ? "border-black/10 hover:border-[#0A0C10] hover:-translate-y-0.5 shadow-sm hover:shadow-[0_18px_36px_-18px_rgba(10,12,16,0.25)] cursor-pointer"
        : failed
          ? "border-red-200 bg-red-50/50"
          : "border-black/8 bg-neutral-50"}`}
    >
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${meta.tint}20`, color: meta.tint }}>
          <Icon size={16} />
        </div>
        {pending && <Loader2 size={14} className="animate-spin text-neutral-400" />}
        {ready && <CheckCircle2 size={16} className="text-emerald-500" />}
        {failed && <XCircle size={16} className="text-red-500" />}
      </div>
      <div className="mt-3 font-display font-semibold text-[15px] text-[#0A0C10]">{item.label}</div>
      <div className="mt-1 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
        {pending && "generating…"}
        {ready && "tap to open"}
        {failed && (item.error || "failed").slice(0, 60)}
      </div>
      {/* "Saved to subject" chip — appears once the backend has cross-filed
          this artifact into its canonical collection. */}
      {ready && item.saved_ref?.id && (
        <div className="mt-3 pt-3 border-t border-black/6 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-emerald-600" data-testid={`lb-saved-${item.key}`}>
          <Bookmark size={11} /> Saved to subject
        </div>
      )}
    </motion.button>
  );
}

/* ────────────────────────── Item viewer modal ────────────────────────── */
function ItemViewer({ item, onClose }) {
  const [playing, setPlaying] = useState(false);
  const meta = ITEM_META[item.key] || {};
  const p = item.payload || {};

  const copyText = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const shareToClass = async () => {
    toast.info("Sharing coming next iteration — for now, open in the module below.");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
        onClick={onClose}
        data-testid="lb-item-viewer"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="bg-white w-full max-w-4xl rounded-lg border border-black/10 shadow-2xl mt-6 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-black/10">
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ {item.key.replace(/_/g, " ")}</div>
              <div className="font-display font-bold text-xl truncate">{item.label}</div>
            </div>
            <div className="flex items-center gap-2">
              {(item.key === "quiz" || item.key === "exit_ticket") && p.questions?.length > 0 && (
                <button onClick={() => setPlaying(true)} data-testid="lb-play-quiz" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFD600] text-[#0A0C10] text-xs font-bold">
                  <Sparkles size={12} /> Play quiz
                </button>
              )}
              {item.key === "slides" && (p.slides || []).length > 0 && (
                <button onClick={() => setPlaying(true)} data-testid="lb-play-slides" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#7C3AED] text-white text-xs font-bold hover:bg-[#6D28D9]">
                  <Layers size={12} /> Play slideshow
                </button>
              )}
              {(p.content || p.answers || p.problems) && (
                <button onClick={() => copyText(p.content || JSON.stringify(p, null, 2))} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-black/15 text-xs font-semibold">
                  <Copy size={12} /> Copy
                </button>
              )}
              <button onClick={shareToClass} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-black/15 text-xs font-semibold">
                <Send size={12} /> Share
              </button>
              <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10]" data-testid="lb-item-close"><X size={18} /></button>
            </div>
          </div>
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {renderPayload(item.key, p)}
          </div>
        </motion.div>
      </motion.div>
      {playing && (item.key === "quiz" || item.key === "exit_ticket") && (
        <StoryQuizPlayer open={playing} onClose={() => setPlaying(false)} quiz={{ id: item.key, title: p.title, questions: p.questions }} timePerQ={60} />
      )}
      {playing && item.key === "slides" && (
        <SlideShowPlayer deck={p} onClose={() => setPlaying(false)} />
      )}
    </AnimatePresence>
  );
}

/* ─────────── Full-screen visual slideshow player ─────────── */
function SlideShowPlayer({ deck, onClose }) {
  const slides = deck.slides || [];
  const [idx, setIdx] = useState(0);
  const s = slides[idx] || {};

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") setIdx((i) => Math.min(i + 1, slides.length - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length, onClose]);

  return (
    <div className="fixed inset-0 z-[95] bg-[#05070D] flex flex-col" data-testid="lb-slideshow">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#22D3EE]">/ slideshow · {deck.title || "deck"}</div>
        <button onClick={onClose} data-testid="lb-slideshow-close" className="text-white/60 hover:text-white p-1.5 rounded hover:bg-white/10"><X size={18} /></button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-4">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
          className="w-full max-w-4xl aspect-video bg-gradient-to-br from-[#0A111D] to-[#0B1526] border border-[#22D3EE]/25 rounded-2xl shadow-[0_0_60px_-15px_rgba(34,211,238,0.4)] p-10 lg:p-14 flex flex-col justify-center overflow-hidden"
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#22D3EE]/70 mb-4">Slide {idx + 1} / {slides.length}</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight text-white" style={{ textShadow: "0 0 30px rgba(34,211,238,0.3)" }}>
            {s.title || s.heading || ""}
          </h2>
          <ul className="mt-6 space-y-3">
            {(s.bullets || s.points || []).map((b, j) => (
              <motion.li
                key={j}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + j * 0.12 }}
                className="flex items-start gap-3 text-white/85 text-lg lg:text-xl"
              >
                <span className="mt-2.5 w-2 h-2 rounded-full bg-[#22D3EE] shrink-0 shadow-[0_0_10px_rgba(34,211,238,0.9)]" />
                <MathText text={b} />
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
      <div className="flex items-center justify-center gap-4 pb-6">
        <button onClick={() => setIdx((i) => Math.max(i - 1, 0))} disabled={idx === 0} data-testid="lb-slide-prev"
          className="px-4 py-2 rounded-full border border-white/20 text-white/80 text-sm hover:bg-white/10 disabled:opacity-30">← Prev</button>
        <div className="flex gap-1.5">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-[#22D3EE] w-6 shadow-[0_0_8px_rgba(34,211,238,0.9)]" : "bg-white/25 hover:bg-white/50"}`} />
          ))}
        </div>
        <button onClick={() => setIdx((i) => Math.min(i + 1, slides.length - 1))} disabled={idx >= slides.length - 1} data-testid="lb-slide-next"
          className="px-4 py-2 rounded-full bg-[#22D3EE] text-[#062A3A] text-sm font-bold hover:brightness-110 disabled:opacity-30">Next →</button>
      </div>
    </div>
  );
}

/* ─────────── Flip-card flashcards ─────────── */
function FlipCard({ front, back }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <button
      onClick={() => setFlipped(!flipped)}
      className="relative h-40 w-full text-left [perspective:900px]"
      data-testid="lb-flashcard"
    >
      <div className="absolute inset-0 transition-transform duration-500 [transform-style:preserve-3d]" style={{ transform: flipped ? "rotateY(180deg)" : "none" }}>
        <div className="absolute inset-0 [backface-visibility:hidden] bg-[#0A0C10] text-white rounded-xl p-4 flex flex-col justify-center">
          <div className="text-[9px] font-mono uppercase tracking-[0.24em] text-[#FFD600] mb-2">Front · tap to flip</div>
          <div className="font-display font-bold text-lg leading-snug"><MathText text={String(front || "")} /></div>
        </div>
        <div className="absolute inset-0 [backface-visibility:hidden] bg-white border-2 border-[#0A0C10] rounded-xl p-4 flex flex-col justify-center" style={{ transform: "rotateY(180deg)" }}>
          <div className="text-[9px] font-mono uppercase tracking-[0.24em] text-[#7C3AED] mb-2">Back</div>
          <div className="text-sm leading-snug"><MathText text={String(back || "")} /></div>
        </div>
      </div>
    </button>
  );
}

/* ─────────── payload renderers ─────────── */
function renderPayload(key, p) {
  if (key === "lesson_plan" || key === "revision" || key === "answer_key") {
    return (
      <div className="prose prose-neutral max-w-none text-[15px] leading-relaxed" data-testid={`lb-content-${key}`}>
        <MathText text={p.content || ""} />
      </div>
    );
  }
  if (key === "quiz" || key === "exit_ticket") {
    return (
      <div className="space-y-4" data-testid={`lb-content-${key}`}>
        <div className="text-sm text-neutral-500 font-mono">{(p.questions || []).length} questions</div>
        {(p.questions || []).map((q, i) => (
          <div key={i} className="border-t border-neutral-100 pt-3">
            <div className="font-medium text-sm">Q{i + 1}. <MathText text={q.question || q.q || ""} /> <span className="text-[10px] uppercase ml-1 text-neutral-400">{q.type || "mcq"}</span></div>
            {q.options && (
              <ol className="mt-1 ml-4 text-sm space-y-0.5 list-decimal">
                {q.options.map((o, j) => <li key={j} className={o === (q.correct_answer || q.answer) ? "text-green-700 font-medium" : ""}>{o}</li>)}
              </ol>
            )}
            {!q.options && <div className="text-xs text-neutral-500 mt-1"><strong>Answer:</strong> {q.correct_answer || q.answer}</div>}
            {q.explanation && <div className="text-xs text-neutral-500 mt-1 italic">{q.explanation}</div>}
          </div>
        ))}
      </div>
    );
  }
  if (key === "slides") {
    return (
      <div className="grid gap-3 sm:grid-cols-2" data-testid={`lb-content-${key}`}>
        {(p.slides || []).map((s, i) => (
          <div key={i} className="bg-neutral-50 border border-black/10 rounded-md p-4">
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1">Slide {i + 1}</div>
            <div className="font-display font-bold text-base">{s.title || s.heading}</div>
            <ul className="mt-2 space-y-1 text-sm text-neutral-700 list-disc pl-4">
              {(s.bullets || s.points || []).map((b, j) => <li key={j}><MathText text={b} /></li>)}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  if (key === "whiteboard") {
    return (
      <div data-testid={`lb-content-${key}`}>
        <div className="font-display font-semibold text-lg mb-3">{p.title}</div>
        <div className="text-xs text-neutral-500 mb-2 font-mono">{(p.elements || []).length} elements</div>
        <ul className="space-y-1 text-sm">
          {(p.elements || []).map((e, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase text-neutral-400 w-16">{e.type}</span>
              <MathText text={e.label || e.text || ""} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (key === "worksheet" || key === "homework") {
    return (
      <div className="space-y-3 text-sm" data-testid={`lb-content-${key}`}>
        <div className="text-neutral-500 font-mono">Difficulty: {p.difficulty || "medium"} · {(p.problems || p.questions || []).length} items</div>
        {(p.problems || p.questions || []).map((prob, i) => (
          <div key={i} className="border-t border-neutral-100 pt-3">
            <div className="font-medium">Q{i + 1}. <MathText text={prob.question || prob.q || prob.prompt || String(prob)} /></div>
            {prob.answer && <div className="text-xs text-neutral-500 mt-1"><strong>Answer:</strong> <MathText text={String(prob.answer)} /></div>}
            {prob.hint && <div className="text-xs text-neutral-500 mt-1 italic">Hint: {prob.hint}</div>}
          </div>
        ))}
      </div>
    );
  }
  if (key === "flashcards") {
    return (
      <div data-testid={`lb-content-${key}`}>
        <div className="text-sm text-neutral-500 font-mono mb-3">{(p.cards || []).length} cards · tap a card to flip it</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(p.cards || []).map((c, i) => (
            <FlipCard key={i} front={c.front || c.term || c.q || c.question} back={c.back || c.definition || c.a || c.answer} />
          ))}
        </div>
      </div>
    );
  }
  return <pre className="text-xs bg-neutral-50 p-3 rounded border border-black/8 overflow-x-auto">{JSON.stringify(p, null, 2)}</pre>;
}


/* ────────────────────────── Attach-to-class modal ────────────────────────── */
function AttachToClassModal({ bundleId, savedCount, onClose }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classId, setClassId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { mine: true, upcoming: true } });
        const list = data.classes || [];
        setClasses(list);
        if (list[0]) setClassId(list[0].id);
      } catch {
        toast.error("Could not load classes");
      } finally { setLoading(false); }
    })();
  }, []);

  const attach = async () => {
    if (!classId) return toast.error("Pick a class");
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/lesson-bundle/${bundleId}/attach-to-class`, { class_id: classId });
      toast.success(
        `Attached ${data.attached} artifacts to ${data.class_title}${data.subject_linked ? " · subject linked" : ""}`
      );
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="lb-attach-modal">
      <div className="bg-white w-full max-w-md rounded-lg p-6 border border-black/8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-black/8">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ attach pack</div>
            <h3 className="font-display font-bold text-xl tracking-tighter mt-0.5">Pin to a class</h3>
            <div className="text-xs text-neutral-500 mt-1">{savedCount} artifacts will attach in one go.</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10]" data-testid="lb-attach-close"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Class *</div>
            {loading ? (
              <div className="text-sm text-neutral-500 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading classes…</div>
            ) : classes.length === 0 ? (
              <div className="text-sm text-neutral-500">You don&rsquo;t have any classes yet. Create one from Classes, then come back.</div>
            ) : (
              <select
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                data-testid="lb-attach-class-select"
                className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]"
              >
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title} · {c.subject || "—"} · {fmtDateTime(c.start_time)}
                  </option>
                ))}
              </select>
            )}
          </label>
          <div className="bg-neutral-50 border border-black/8 rounded-md p-3 text-xs text-neutral-600 leading-relaxed">
            <strong>What this does:</strong> pins the whole pack to the class so students see every artifact in the live drawer. If the class isn&rsquo;t linked to a subject yet, we&rsquo;ll link it to this bundle&rsquo;s subject automatically.
          </div>
          <button
            onClick={attach}
            disabled={busy || !classId || loading || classes.length === 0}
            data-testid="lb-attach-confirm"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#0A0C10] text-[#FFD600] font-bold disabled:opacity-50 hover:bg-[#0A0C10]/95"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Bookmark size={14} />}
            {busy ? "Attaching…" : `Attach ${savedCount} artifacts`}
          </button>
        </div>
      </div>
    </div>
  );
}
